import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "psim_chat_session";

export const uid = () =>
  (crypto.randomUUID && crypto.randomUUID()) || `id-${Date.now()}-${Math.random()}`;

const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = uid();
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

export const GREETING = {
  id: "greeting",
  role: "assistant",
  content:
    "Hi, I'm Emmy 👋 — your AI assistant for Precision Process Safety & Integrity. Ask me about PHA, LOPA, SIS, dust hazards, PSM/RMP compliance, relief systems, flare design, or any of our services.",
};

const FALLBACK_ERROR = "Sorry, I ran into an issue. Please try again in a moment.";

const noticeFromError = (e) => {
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;
  const isLimit = status === 429 || status === 413;
  return {
    text: isLimit && typeof detail === "string" ? detail : FALLBACK_ERROR,
    quotaExhausted: status === 429 && e?.response?.headers?.["x-quota-remaining"] === "0",
  };
};

// Chat state + API calls for the Emmy support widget (session, quota, send, clear).
export const useChat = (open) => {
  const [sessionId] = useState(getSessionId);
  const [messages, setMessages] = useState([GREETING]);
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/chat/${sessionId}/usage`)
      .then(({ data }) => setQuota({ remaining: data.quota_remaining, limit: data.quota_limit }))
      .catch(() => {});
  }, [open, sessionId]);

  const send = useCallback(
    async (content) => {
      const text = content.trim();
      if (!text || loading) return;
      setMessages((m) => [...m, { id: uid(), role: "user", content: text }]);
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: text });
        setMessages((m) => [...m, { id: uid(), role: "assistant", content: data.reply }]);
        setQuota({ remaining: data.quota_remaining, limit: data.quota_limit });
      } catch (e) {
        const notice = noticeFromError(e);
        if (notice.quotaExhausted) setQuota((q) => ({ remaining: 0, limit: q?.limit ?? 0 }));
        setMessages((m) => [...m, { id: uid(), role: "assistant", content: notice.text, isNotice: true }]);
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionId]
  );

  const clear = useCallback(async () => {
    try {
      await axios.delete(`${API}/chat/${sessionId}`);
    } catch (e) {
      // Local reset still proceeds; the server copy is retried on next clear.
    }
    setMessages([GREETING]);
  }, [sessionId]);

  return { messages, loading, quota, send, clear };
};
